import { ReactNode, createContext, useContext, useEffect, useState, useMemo } from 'react';
import { useMsal, useIsAuthenticated as useMsalIsAuthenticated } from '@azure/msal-react';
import { InteractionRequiredAuthError, InteractionStatus } from '@azure/msal-browser';
import { DEFAULT_SCOPES } from './scopes';
import { toast } from "sonner"
import { isNativePlatform } from '../platform-detection';

const HAS_LOGGED_IN_KEY = 'city_sage_has_logged_in';

interface AuthContextType {
  getToken: () => Promise<string | null>;
  login: () => Promise<void>;
  signIn: () => Promise<void>;
  logout: () => Promise<void>;
  userEmail: string | null;
  userName: string | null;
  isInProgress: boolean;
  silentAuthAttempted: boolean;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Custom hook that works for both web and Capacitor
export const useIsAuthenticated = () => {
  const { isAuthenticated } = useAuth();
  return isAuthenticated;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  // Detect if running in Capacitor (native mobile)
  const isCapacitor = useMemo(() => isNativePlatform(), []);
  
  // Web MSAL hooks - must be called unconditionally due to React rules of hooks
  // On Capacitor, these will use default/empty values from MsalProvider
  const msalContext = useMsal();
  const webIsAuthenticated = useMsalIsAuthenticated();
  
  // Extract MSAL values (will be defaults on Capacitor)
  const instance = msalContext?.instance;
  const accounts = msalContext?.accounts || [];
  const inProgress = msalContext?.inProgress || InteractionStatus.None;
  
  // Shared state
  const [silentAuthAttempted, setSilentAuthAttempted] = useState(false);
  const [userName, setUserName] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [mobileAccessToken, setMobileAccessToken] = useState<string | null>(null);
  const [mobileIdToken, setMobileIdToken] = useState<string | null>(null);
  const [isMobileAuthenticated, setIsMobileAuthenticated] = useState(false);

  // Log auth state changes
  useEffect(() => {
    console.log('[AUTH] Auth state updated:', {
      isCapacitor,
      isAuthenticated: isCapacitor ? isMobileAuthenticated : webIsAuthenticated,
      inProgress: isCapacitor ? false : inProgress,
      accountsCount: isCapacitor ? 0 : accounts.length,
      activeAccount: isCapacitor ? 'mobile' : accounts[0]?.username || 'none',
      timestamp: new Date().toISOString()
    });
  }, [isCapacitor, webIsAuthenticated, isMobileAuthenticated, inProgress, accounts]);

  // Web does not contact the identity provider until login or sign-up.
  // Native restores a session only after the person has logged in before.
  useEffect(() => {
    if (isCapacitor) {
      // Check if user has logged in before - only attempt silent login if flag exists
      const hasLoggedIn = localStorage.getItem(HAS_LOGGED_IN_KEY) === 'true';
      
      if (!hasLoggedIn) {
        // New user - skip silent login attempt to avoid popup
        console.log('[AUTH] Capacitor platform detected - no previous login flag found, skipping silent login');
        setSilentAuthAttempted(true);
        return;
      }
      
      // For Capacitor, attempt silent login using prompt: 'none'
      // This will succeed if there are cached credentials, fail silently otherwise
      const attemptSilentMobileLogin = async () => {
        console.log('[AUTH] Capacitor platform detected - attempting silent login (previous login flag found)');
        
        try {
          const { MsAuthPlugin } = await import('@recognizebv/capacitor-plugin-msauth');
          const clientId = import.meta.env.VITE_ENTRA_CLIENT_ID;
          const authorityUrl = import.meta.env.VITE_ENTRA_AUTHORITY;
          const keyHash = import.meta.env.VITE_ANDROID_KEY_HASH;
          
          if (!clientId || !authorityUrl) {
            console.log('[AUTH] Silent login skipped - missing configuration');
            setSilentAuthAttempted(true);
            return;
          }
          
          const authorityType = authorityUrl.includes('ciamlogin.com') ? 'CIAM' : 'AAD';
          
          // Attempt silent login with prompt: 'none'
          const result = await MsAuthPlugin.login({
            clientId,
            authorityUrl,
            authorityType,
            scopes: DEFAULT_SCOPES,
            keyHash: keyHash || undefined,
            prompt: 'none', // Key: attempt without user interaction
          });
          
          console.log('[AUTH] Silent login successful');
          setMobileAccessToken(result.accessToken);
          setMobileIdToken(result.idToken);
          setIsMobileAuthenticated(true);
          
          const decodedToken = decodeJWT(result.idToken);
          if (decodedToken) {
            extractUserInfo(decodedToken);
          }
        } catch (error) {
          // Silent login failed - this is expected if no cached credentials exist
          // Don't show error, just log it
          console.log('[AUTH] Silent login failed (expected if no cached credentials):', error);
        } finally {
          setSilentAuthAttempted(true);
        }
      };
      
      attemptSilentMobileLogin();
      return;
    }

    // Do not open a hidden iframe to Microsoft until the visitor starts login.
    // Accounts already stored in sessionStorage are restored locally by MSAL.
    if (!silentAuthAttempted) {
      setSilentAuthAttempted(true);
    }
  }, [isCapacitor, silentAuthAttempted]);

  // Extract user information from token claims
  const extractUserInfo = (tokenClaims: Record<string, unknown>) => {
    // Handle email
    if ("email" in tokenClaims) {
      setUserEmail(tokenClaims.email as string);
    }
    
    // Handle name - prioritize given_name + family_name if available
    if ("given_name" in tokenClaims && "family_name" in tokenClaims) {
      const givenName = tokenClaims.given_name as string;
      const familyName = tokenClaims.family_name as string;
      setUserName(`${givenName} ${familyName}`);
    } 
    // Fallback to name field if it's meaningful
    else if ("name" in tokenClaims && tokenClaims.name !== "unknown") {
      setUserName(tokenClaims.name as string);
    } 
  };

  // Decode JWT token to extract claims (for mobile)
  const decodeJWT = (token: string): Record<string, unknown> | null => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('[AUTH] Failed to decode JWT token', error);
      return null;
    }
  };

  const getToken = async (): Promise<string | null> => {
    // Mobile (Capacitor) implementation
    if (isCapacitor) {
      // If we already have a token cached, return it
      if (mobileAccessToken) {
        console.log('[AUTH] getToken: Returning cached mobile token');
        return mobileAccessToken;
      }
      
      console.log('[AUTH] getToken: No mobile token available, user needs to login');
      return null;
    }

    // Web implementation (existing MSAL logic)
    if (accounts.length === 0) {
      console.log('[AUTH] getToken: No accounts available');
      return null;
    }
    
    console.log('[AUTH] getToken: Attempting to acquire token silently', {
      account: accounts[0].username,
      scopes: DEFAULT_SCOPES
    });
    
    try {
      const response = await instance.acquireTokenSilent({
        scopes: DEFAULT_SCOPES,
        account: accounts[0],
        state: JSON.stringify({
          redirectUrl: window.location.pathname + window.location.search
        })
      });
      console.log('[AUTH] getToken: Token acquired silently', {
        tokenExpiration: response.expiresOn,
        tokenType: response.tokenType
      });

      // Extract user info from token claims
      const tokenClaims = response.idTokenClaims;
      if (tokenClaims && typeof tokenClaims === 'object') {
        extractUserInfo(tokenClaims as Record<string, unknown>);
      }

      return response.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        console.log('[AUTH] getToken: Interaction required, trying popup');
        try {
          const response = await instance.acquireTokenPopup({
            scopes: DEFAULT_SCOPES,
            account: accounts[0]
          });
          console.log('[AUTH] getToken: Token acquired via popup', {
            tokenExpiration: response.expiresOn,
            tokenType: response.tokenType
          });

          // Extract user info from token claims
          const tokenClaims = response.idTokenClaims;
          if (tokenClaims && typeof tokenClaims === 'object') {
            extractUserInfo(tokenClaims as Record<string, unknown>);
          }

          return response.accessToken;
        } catch (popupError) {
          console.error('[AUTH] getToken: Popup acquisition failed', popupError);
          toast.error('Authentication failed. Please try again.');
        }
      } else {
        console.error('[AUTH] getToken: Silent acquisition failed', error);
      }
      return null;
    }
  };

  const login = async () => {
    // Mobile (Capacitor) implementation
    if (isCapacitor) {
      try {
        console.log('[AUTH] login: Initiating mobile MSAL login');
        
        // Dynamically import the Capacitor MSAL plugin
        const { MsAuthPlugin } = await import('@recognizebv/capacitor-plugin-msauth');
        
        const clientId = import.meta.env.VITE_ENTRA_CLIENT_ID;
        const authorityUrl = import.meta.env.VITE_ENTRA_AUTHORITY;
        const keyHash = import.meta.env.VITE_ANDROID_KEY_HASH; // Optional, for Android
        
        if (!clientId || !authorityUrl) {
          console.error('[AUTH] login: Missing required environment variables');
          toast.error('Authentication configuration error');
          return;
        }

        // Determine authority type based on URL
        const authorityType = authorityUrl.includes('ciamlogin.com') ? 'CIAM' : 'AAD';
        
        const result = await MsAuthPlugin.login({
          clientId,
          authorityUrl,
          authorityType,
          scopes: DEFAULT_SCOPES,
          keyHash: keyHash || undefined, // Only for Android
        });

        console.log('[AUTH] login: Mobile MSAL login successful');
        
        // Store both access and ID tokens
        setMobileAccessToken(result.accessToken);
        setMobileIdToken(result.idToken);
        setIsMobileAuthenticated(true);
        
        // Set flag to indicate user has logged in (for future silent login attempts)
        localStorage.setItem(HAS_LOGGED_IN_KEY, 'true');
        
        // Decode the ID token to extract user info (ID tokens contain user claims)
        const decodedToken = decodeJWT(result.idToken);
        if (decodedToken) {
          extractUserInfo(decodedToken);
        }
        
        toast.success('Login successful');
      } catch (error) {
        console.error('[AUTH] login: Mobile MSAL login failed', error);
        toast.error('Login failed. Please try again.');
      }
      return;
    }

    // Web implementation (existing MSAL logic)
    const currentUrl = window.location.pathname + window.location.search;
    
    const state = JSON.stringify({
      redirectUrl: currentUrl
    });
    
    console.log('[AUTH] login: Initiating login redirect', {
      currentPath: window.location.pathname,
      currentSearch: window.location.search,
      fullRedirectUrl: currentUrl,
      redirectState: state,
      timestamp: new Date().toISOString()
    });
    
    instance.loginRedirect({
      scopes: DEFAULT_SCOPES,
      state
    });
  };


  const signIn = async () => {
    // Mobile (Capacitor) implementation - same as login for now
    if (isCapacitor) {
      // For mobile, signIn is the same as login
      await login();
      return;
    }

    // Web implementation (existing MSAL logic)
    const currentUrl = window.location.pathname + window.location.search;
    
    const state = JSON.stringify({
      redirectUrl: currentUrl
    });
    
    console.log('[AUTH] signIn: Initiating login redirect with create prompt', {
      currentPath: window.location.pathname,
      currentSearch: window.location.search,
      fullRedirectUrl: currentUrl,
      redirectState: state,
      timestamp: new Date().toISOString()
    });
    
    instance.loginRedirect({
      scopes: DEFAULT_SCOPES,
      state,
      prompt: "create"
    });
  };


  const logout = async () => {
    // Mobile (Capacitor) implementation
    if (isCapacitor) {
      try {
        console.log('[AUTH] logout: Initiating mobile MSAL logout');
        
        // Dynamically import the Capacitor MSAL plugin
        const { MsAuthPlugin } = await import('@recognizebv/capacitor-plugin-msauth');
        
        const clientId = import.meta.env.VITE_ENTRA_CLIENT_ID;
        const authorityUrl = import.meta.env.VITE_ENTRA_AUTHORITY;
        const keyHash = import.meta.env.VITE_ANDROID_KEY_HASH; // Optional, for Android

        if (!clientId || !authorityUrl) {
          console.error('[AUTH] logout: Missing required environment variables');
          // Still clear local state even if config is missing
          setMobileAccessToken(null);
          setMobileIdToken(null);
          setIsMobileAuthenticated(false);
          setUserName(null);
          setUserEmail(null);
          return;
        }

        // Determine authority type based on URL (same as login)
        const authorityType = authorityUrl.includes('ciamlogin.com') ? 'CIAM' : 'AAD';

        // Attempt to logout from MSAL
        // NOTE: authorityType is REQUIRED for MSAL to create the context properly
        await MsAuthPlugin.logout({
          clientId,
          authorityUrl,
          authorityType, // CRITICAL: Without this, MSAL can't create context!
          keyHash: keyHash || undefined, // Only for Android
        });

        console.log('[AUTH] logout: Mobile MSAL logout successful');
        toast.success('Logged out successfully');
      } catch (error) {
        // Native logout failed, but we'll still clear local state
        console.warn('[AUTH] logout: Mobile MSAL logout failed (clearing local state anyway)', error);
        // Don't show error toast since we're still logging out locally
      } finally {
        // ALWAYS clear local state regardless of native logout success/failure
        console.log('[AUTH] logout: Clearing local authentication state');
        setMobileAccessToken(null);
        setMobileIdToken(null);
        setIsMobileAuthenticated(false);
        setUserName(null);
        setUserEmail(null);
        // Remove the login flag so silent login won't be attempted on next app start
        localStorage.removeItem(HAS_LOGGED_IN_KEY);
      }
      return;
    }

    // Web implementation (existing MSAL logic)
    console.log('[AUTH] logout: Initiating logout', {
      account: accounts[0]?.username || 'unknown',
      timestamp: new Date().toISOString()
    });
    
    instance.logoutRedirect();
  };

  const isInProgress = isCapacitor ? false : inProgress !== 'none';
  const effectiveIsAuthenticated = isCapacitor ? isMobileAuthenticated : webIsAuthenticated;

  const value: AuthContextType = {
    getToken,
    login,
    signIn,
    logout,
    userEmail,
    userName,
    isInProgress,
    silentAuthAttempted,
    isAuthenticated: effectiveIsAuthenticated
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};